/* eslint-disable react/require-default-props */
import React from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import {
  Badge,
  Button,
  FlexColumn,
  FlexRow,
  Typography,
} from "design-system";
import {
  BookOpenIcon,
  CloseCircleIcon,
  UndoIcon,
} from "design-system/components/Icons";
import LearningShell from "../../components/LearningShell";
import WarningBanner from "../../components/WarningBanner";
import { formatLearningScoreSummary } from "../../helpers/mapLearningSubmitResponse";

export default function FailStep({
  course,
  quizResult,
  passMarkPercent,
  attemptNumber,
  maxAttempts,
  attemptsRemaining,
  canRetry = false,
  onRetry,
  onReviewGuidance,
}) {
  const intl = useIntl();
  const remainingLabel = intl.formatMessage(
    { id: 'ld-attempts-remaining' },
    { count: attemptsRemaining },
  );

  return (
    <LearningShell
      layout="outcome"
      showWarning={false}
    >
      <FlexColumn align="center" fullWidth className="ld-outcome">
        <FlexColumn
          align="center"
          fullWidth
          gap="var(--ds-space-4)"
          className="ld-outcome__hero"
        >
          <FlexRow
            align="center"
            justify="center"
            fullWidth={false}
            className="ld-outcome__icon ld-outcome__icon--fail"
          >
            <CloseCircleIcon
              size={32}
              aria-hidden
              className="ds-icon--color-warning"
            />
          </FlexRow>

          <Badge variant="notice" emphasis="subtle" size="md" dot>
            {intl.formatMessage({ id: 'ld-not-passed' })}
          </Badge>

          <Typography
            component="h1"
            variant="headingXLarge"
            fontWeight="bold"
            color="ui-emphasis"
            className="ld-outcome__title"
          >
            {intl.formatMessage({ id: 'ld-fail-title' })}
          </Typography>

          <Typography
            component="p"
            variant="bodyMedium"
            color="ui-subtle"
            className="ld-outcome__description"
          >
            {intl.formatMessage(
              { id: 'ld-fail-description' },
              {
                score: formatLearningScoreSummary(quizResult),
                passMark: passMarkPercent,
                remaining: remainingLabel,
              },
            )}
          </Typography>

          <FlexRow
            align="center"
            fullWidth={false}
            gap="var(--ds-space-2)"
            className="ld-outcome__attempt"
          >
            {Array.from({ length: maxAttempts }, (_, index) => (
              <span
                key={index}
                className={
                  index < attemptNumber
                    ? "ld-outcome__attempt-dot"
                    : "ld-outcome__empty-dot"
                }
                aria-hidden
              />
            ))}
            <Typography component="span" variant="bodySmall" color="ui-subtle">
              {intl.formatMessage(
                { id: 'ld-attempt-of' },
                { current: attemptNumber, total: maxAttempts },
              )}
            </Typography>
          </FlexRow>
        </FlexColumn>

        <WarningBanner
          className="ld-outcome__alert"
          message={intl.formatMessage({ id: 'ld-fail-alert' })}
        />

        <FlexRow
          align="center"
          justify="center"
          fullWidth
          wrap
          gap="var(--ds-space-3)"
          className="ld-outcome__actions"
        >
          {canRetry ? (
            <Button
              variant="primary"
              size="base"
              icon={<UndoIcon size={16} />}
              iconPosition="right"
              onClick={onRetry}
            >
              {intl.formatMessage({ id: 'ld-try-again' })}
            </Button>
          ) : null}
          <Button
            variant="secondary"
            size="base"
            icon={<BookOpenIcon size={15} />}
            iconPosition="left"
            onClick={onReviewGuidance}
          >
            {intl.formatMessage({ id: 'ld-review-guidance' })}
          </Button>
        </FlexRow>
      </FlexColumn>
    </LearningShell>
  );
}

FailStep.propTypes = {
  course: PropTypes.shape({
    description: PropTypes.string,
    logoSrc: PropTypes.string,
  }).isRequired,
  quizResult: PropTypes.shape({
    scorePercent: PropTypes.number.isRequired,
    correctCount: PropTypes.number.isRequired,
    totalQuestions: PropTypes.number.isRequired,
  }).isRequired,
  passMarkPercent: PropTypes.number.isRequired,
  attemptNumber: PropTypes.number.isRequired,
  maxAttempts: PropTypes.number.isRequired,
  attemptsRemaining: PropTypes.number.isRequired,
  canRetry: PropTypes.bool,
  onRetry: PropTypes.func.isRequired,
  onReviewGuidance: PropTypes.func.isRequired,
};
